import "./ListPosts.scss"

import React, { useState } from "react"
import { Post, Tag, CurrentUser } from "@fider/models"
import { ShowTag, Markdown, Icon, ResponseLozenge, Avatar, Moment } from "@fider/components"
import IconChatAlt2 from "@fider/assets/images/heroicons-chat-alt-2.svg"
import IconThumbsUp from "@fider/assets/images/heroicons-thumbsup.svg"
import { HStack, VStack } from "@fider/components/layout"
import { useFider } from "@fider/hooks"
import { classSet } from "@fider/services"
import { isCategorySlug } from "@fider/services/khcn"
import { CategoryArt } from "./CategoryArt"
import { i18n } from "@lingui/core"
import { Trans } from "@lingui/react/macro"

interface ListPostsProps {
  posts?: Post[]
  tags: Tag[]
  emptyText: string
  minimalView?: boolean
  showStatus?: boolean
  maxVisible?: number
  onPostClick?: (postNumber: number, slug: string) => void
}

// Tùy biến KHCN: lấy ảnh đầu tiên trong nội dung bài (ảnh đính kèm nội tuyến "fider-image:", ảnh tĩnh /static/... hoặc URL ngoài)
// làm thumbnail. Bài không có ảnh dùng minh hoạ theo hạng mục. Không cần thêm trường ở backend.
const IMAGE_RE = /!\[[^\]]*\]\(\s*(fider-image:[^)\s]+|https?:\/\/[^)\s]+|\/[^)\s]+)\s*\)/

const firstImage = (markdown: string, assetsURL: string): string | undefined => {
  const match = IMAGE_RE.exec(markdown || "")
  if (!match) {
    return undefined
  }
  const href = match[1]
  if (href.startsWith("fider-image:")) {
    return `${assetsURL}/static/images/${href.substring("fider-image:".length)}?size=600`
  }
  // Ảnh tĩnh của site (vd. /static/assets/posts/x.jpg) đi qua assetsURL để hợp CDN nếu có
  if (href.startsWith("/static/")) {
    return `${assetsURL}${href}`
  }
  return href
}

const ListPostItem = (props: {
  post: Post
  user?: CurrentUser
  tags: Tag[]
  showStatus?: boolean
  featured?: boolean
  onPostClick?: (postNumber: number, slug: string) => void
}) => {
  const fider = useFider()
  const isPending = !props.post.isApproved
  // Đơn vị · tỉnh thành hiển thị cạnh tên tác giả
  const meta = [props.post.user.organization, props.post.user.province].filter((part) => !!part).join(" · ")
  const categorySlug = props.post.tags.find((slug) => isCategorySlug(slug))
  const thumbnail = firstImage(props.post.description, fider.settings.assetsURL)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.onPostClick) {
      e.preventDefault()
      props.onPostClick(props.post.number, props.post.slug)
    }
  }

  return (
    <a href={`/posts/${props.post.number}/${props.post.slug}`} className="kh-card" onClick={handleClick}>
      <div className="kh-card__thumb">
        {thumbnail ? <img src={thumbnail} alt="" loading="lazy" /> : <CategoryArt slug={categorySlug} />}
        {props.featured && (
          <span className="kh-card__badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9.7 18h4.6M10 21h4M12 3a6 6 0 0 0-3.6 10.8c.7.6 1.1 1.4 1.3 2.2h4.6c.2-.8.6-1.6 1.3-2.2A6 6 0 0 0 12 3z" />
            </svg>
            {i18n._({ id: "khcn.card.featured", message: "Featured" })}
          </span>
        )}
      </div>
      <div className="kh-card__body">
        <h3 className="kh-card__title text-break">
          {props.post.title}
          {isPending && (
            <span className="kh-card__pending">
              <Trans id="post.pending">pending</Trans>
            </span>
          )}
        </h3>
        <div className="kh-card__meta">
          <Avatar user={props.post.user} size="small" />
          <span className="kh-card__author">{props.post.user.name}</span>
          {meta && <span className="kh-card__org">· {meta}</span>}
        </div>
        <Markdown className="kh-card__excerpt" maxLength={220} text={props.post.description} style="plainText" />
        <div className="kh-card__foot">
          <div className="kh-card__tags">
            {props.tags.map((tag) => (
              <ShowTag key={tag.id} tag={tag} />
            ))}
          </div>
          <span className="kh-card__spacer" />
          <span className={classSet({ "kh-card__stat": true, "kh-card__stat--voted": props.post.hasVoted })}>
            <Icon sprite={IconThumbsUp} />
            {props.post.votesCount}
          </span>
          <span className="kh-card__stat">
            <Icon sprite={IconChatAlt2} />
            {props.post.commentsCount}
          </span>
          {props.showStatus !== false && props.post.status !== "open" && (
            <ResponseLozenge status={props.post.status} response={props.post.response} size={"small"} />
          )}
          <Moment className="kh-card__time" locale={fider.currentLocale} date={props.post.createdAt} />
        </div>
      </div>
    </a>
  )
}

const MinimalListPostItem = (props: { post: Post; tags: Tag[]; onPostClick?: (postNumber: number, slug: string) => void }) => {
  const isPending = !props.post.isApproved

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (props.onPostClick) {
      e.preventDefault()
      props.onPostClick(props.post.number, props.post.slug)
    }
  }

  return (
    <HStack spacing={4} align="start" className="c-posts-container__post-minimal">
      <HStack className="w-full" justify="between" align="start">
        <HStack spacing={2} align="start" justify="between" className="w-full">
          <a className="text-link" href={`/posts/${props.post.number}/${props.post.slug}`} onClick={handleClick}>
            {props.post.title}
          </a>
          {isPending && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">pending</span>}
        </HStack>
        {props.post.status !== "open" ? (
          <div>
            <ResponseLozenge status={props.post.status} response={props.post.response} size={"micro"} />
          </div>
        ) : (
          <span className="text-gray-700 text-sm">+{props.post.votesCount}</span>
        )}
      </HStack>
    </HStack>
  )
}

export const ListPosts = (props: ListPostsProps) => {
  const { minimalView = false } = props
  const [expanded, setExpanded] = useState(false)

  if (!props.posts) {
    return null
  }

  if (props.posts.length === 0) {
    return <p className="text-center">{props.emptyText}</p>
  }

  const allPosts = props.posts
  const hasMore = props.maxVisible !== undefined && !expanded && allPosts.length > props.maxVisible
  const visiblePosts = hasMore ? allPosts.slice(0, props.maxVisible) : allPosts
  const remainingCount = allPosts.length - visiblePosts.length

  // "Nổi bật" = bài nhiều bình chọn nhất trong danh sách đang hiển thị (chỉ khi có ít nhất 1 bình chọn)
  const topPost = visiblePosts.reduce<Post | undefined>((best, post) => (post.votesCount > (best ? best.votesCount : 0) ? post : best), undefined)
  const featuredId = topPost && topPost.votesCount > 0 ? topPost.id : undefined

  return (
    <>
      {minimalView ? (
        <VStack spacing={2}>
          {visiblePosts.map((post) => (
            <MinimalListPostItem
              key={post.id}
              post={post}
              tags={props.tags.filter((tag) => post.tags.indexOf(tag.slug) >= 0)}
              onPostClick={props.onPostClick}
            />
          ))}
        </VStack>
      ) : (
        <>
          {visiblePosts.map((post) => (
            <ListPostItem
              key={post.id}
              post={post}
              tags={props.tags.filter((tag) => post.tags.indexOf(tag.slug) >= 0)}
              showStatus={props.showStatus}
              featured={post.id === featuredId}
              onPostClick={props.onPostClick}
            />
          ))}
        </>
      )}
      {hasMore && (
        <div className="my-4 text-center">
          <a
            href="#"
            className="text-primary-base text-medium hover:underline"
            onClick={(e) => {
              e.preventDefault()
              setExpanded(true)
            }}
          >
            <Trans id="listposts.label.showmore">Show {remainingCount} more</Trans>
          </a>
        </div>
      )}
    </>
  )
}
